/**
 * Step → screen. Pure, like `./step`: which screen a classified step mounts.
 *
 * `stepFor` says what KIND of step the applicant is on (collect / review /
 * terminal …); this says which screen that is, from the events the step
 * accepts. It used to live inline in `FlowRunner` as an if-chain; it is pulled
 * out so the console's flow preview (`routes/PreviewFlowScreen`) picks the SAME
 * screen the live flow would — a preview with its own chain is a drawing of the
 * flow that drifts the first time a step is added here.
 *
 * Order is load-bearing and is the runner's order: consent wins over capture,
 * capture over phone and signature, and an applicant action nobody has a screen
 * for falls through to a plain button per event.
 */
import { BUSINESS_DOC_EVENT } from './businessDocument';
import { COMPANY_EVENT } from './company';
import { DOCUMENTS_EVENT } from './DocumentGroupsStep';
import { KEY_PEOPLE_DONE_EVENT, KEY_PEOPLE_EVENT } from './keyPeople';
import { CHIP_READ_EVENT } from './dipChip';
import { DOCUMENT_CHIP_READ_EVENT } from './documentChip';
import { documentAwaitingBack, offeredDocuments, type DocumentKind } from './documents';
import { PHONE_VERIFIED_EVENT } from './PhoneStep';
import { SIGNATURE_SUBMITTED_EVENT } from './SignatureStep';
import type { StepView } from './types';

/** Consent gate: CONSENT_GIVEN mounts the trustsy identity-consent card. */
export const CONSENT_EVENT = 'CONSENT_GIVEN';

/** The liveness step advances via LIVENESS_DONE. */
export const LIVENESS_EVENT = 'LIVENESS_DONE';

/** A questionnaire's answers (./questionnaire/QuestionnaireStep). */
export const QUESTIONNAIRE_EVENT = 'QUESTIONNAIRE_SUBMITTED';

/** Self-asserted attributes (IAL 1) — mounts the details form. */
export const DETAILS_EVENT = 'DETAILS_SUBMITTED';

/** Tag the builder stamps on engine-run states (plugin calls, scoring). */
export const AUTOMATED_CHECK_TAG = 'pending_process';

export type Screen =
  | { kind: 'terminal'; outcome: NonNullable<StepView['outcome']> }
  | { kind: 'blocked' }
  /** An automated check: seconds long, and the applicant must keep the page open. */
  | { kind: 'checking' }
  | { kind: 'review' }
  | { kind: 'consent' }
  /** The front of a document; several offered means the applicant picks one first. */
  | { kind: 'document'; offered: DocumentKind[] }
  | { kind: 'documentBack'; document: DocumentKind }
  /** The business registration certificate (KYB): a photo or a PDF. */
  | { kind: 'businessDocument' }
  /** The company itself (KYB): registry search, then confirm or enter by hand. */
  | { kind: 'company' }
  /** The company's UBOs / directors / representatives (KYB). */
  | { kind: 'keyPeople' }
  /** Waiting while each key person completes their own KYC. */
  | { kind: 'awaitingUsers' }
  /** One upload per document group (KYB). */
  | { kind: 'documentGroups' }
  /** The project's own questions (./questionnaire). */
  | { kind: 'questionnaire' }
  | { kind: 'phone' }
  /** An ePassport / eID chip over NFC — a native SDK reads it, or the step is skipped. */
  | { kind: 'documentChip' }
  /** A Thai ID card in a contact reader — read on the partner's terminal, never here. */
  | { kind: 'dipChip' }
  | { kind: 'signature' }
  | { kind: 'liveness' }
  | { kind: 'details' }
  /** No dedicated screen: one button per applicant event. */
  | { kind: 'actions'; actions: string[] };

export function screenFor(step: StepView): Screen {
  if (step.kind === 'terminal') return { kind: 'terminal', outcome: step.outcome ?? 'completed' };
  if (step.kind === 'blocked') return { kind: 'blocked' };
  if (step.kind === 'review') {
    return step.tags.includes(AUTOMATED_CHECK_TAG) ? { kind: 'checking' } : { kind: 'review' };
  }

  const { actions } = step;
  if (actions.includes(CONSENT_EVENT)) return { kind: 'consent' };
  const offered = offeredDocuments(actions);
  if (offered.length > 0) return { kind: 'document', offered };
  const back = documentAwaitingBack(actions);
  if (back) return { kind: 'documentBack', document: back };
  if (actions.includes(COMPANY_EVENT)) return { kind: 'company' };
  if (actions.includes(KEY_PEOPLE_EVENT)) return { kind: 'keyPeople' };
  if (actions.includes(KEY_PEOPLE_DONE_EVENT)) return { kind: 'awaitingUsers' };
  if (actions.includes(DOCUMENTS_EVENT)) return { kind: 'documentGroups' };
  if (actions.includes(QUESTIONNAIRE_EVENT)) return { kind: 'questionnaire' };
  if (actions.includes(BUSINESS_DOC_EVENT)) return { kind: 'businessDocument' };
  if (actions.includes(PHONE_VERIFIED_EVENT)) return { kind: 'phone' };
  if (actions.includes(DOCUMENT_CHIP_READ_EVENT)) return { kind: 'documentChip' };
  if (actions.includes(CHIP_READ_EVENT)) return { kind: 'dipChip' };
  if (actions.includes(SIGNATURE_SUBMITTED_EVENT)) return { kind: 'signature' };
  if (actions.includes(LIVENESS_EVENT)) return { kind: 'liveness' };
  if (actions.includes(DETAILS_EVENT)) return { kind: 'details' };
  return { kind: 'actions', actions };
}
